/**
 * Builds the rich "Look & Mood" panel — concept pads, scene presets,
 * composition + color segmented controls, and categorized sliders.
 *
 * Designed to be the visualizer's main creative surface: every slider has a
 * label, sub-label, value readout, and a fill bar that mirrors its current
 * position (legacy PulseFX VJ style).
 */

import { Renderer } from "../viz/renderer";
import {
  BioclastParams,
  DEFAULT_BIOCLAST,
} from "../viz/scenes/bioclast";
import {
  SCENES,
  scenesByMood,
} from "../viz/scenes/scenePresets";
import { applyScenePreset } from "../viz/scenes/applyScenePreset";
import {
  filterBySceneProfile,
  type ControlDef,
  type ControlSection,
} from "./advancedControls";

// ---------------------------------------------------------------------------
// Scene presets — multi-knob recipes triggered as pads
// ---------------------------------------------------------------------------

export interface ScenePreset {
  name: string;
  /** Optional accent hue used to tint the pad button. */
  accent?: number;
  values: Partial<BioclastParams>;
}

export const SCENE_PRESETS: ScenePreset[] = [
  {
    name: "Rivers",
    accent: 200,
    values: {
      visualMode: 3,
      colorMode: 1,
      paletteHue: 200,
      starSize: 0.18,
      cymaticStrength: 1.6,
      flowSpeed: 1.0,
      atmosphere: 0.6,
      conceptMode: "none",
    },
  },
  {
    name: "Strings",
    accent: 190,
    values: {
      visualMode: 1,
      colorMode: 1,
      paletteHue: 190,
      starSize: 0.14,
      flowSpeed: 0.9,
      cymaticStrength: 0.8,
      atmosphere: 0.5,
      conceptMode: "none",
    },
  },
  {
    name: "Lattice",
    accent: 165,
    values: {
      visualMode: 2,
      colorMode: 3,
      paletteHue: 170,
      starSize: 0.1,
      flowSpeed: 0.5,
      cymaticStrength: 1.0,
      atmosphere: 0.5,
      conceptMode: "none",
    },
  },
  {
    name: "Cathedral",
    accent: 175,
    values: {
      visualMode: 2,
      colorMode: 3,
      paletteHue: 175,
      starSize: 0.13,
      flowSpeed: 0.4,
      cymaticStrength: 1.0,
      atmosphere: 0.4,
      backdropBlur: 30,
      conceptMode: "cathedral",
    },
  },
  {
    name: "Storm",
    accent: 230,
    values: {
      visualMode: 3,
      colorMode: 2,
      paletteHue: 230,
      starSize: 0.2,
      cymaticStrength: 3.2,
      bassGravity: 2.4,
      midCurl: 1.6,
      flowSpeed: 1.5,
      atmosphere: 0.85,
      conceptMode: "none",
    },
  },
  {
    name: "Cymatics",
    accent: 180,
    values: {
      visualMode: 3,
      colorMode: 1,
      paletteHue: 180,
      starSize: 0.15,
      cymaticStrength: 3.5,
      bassGravity: 2.0,
      flowSpeed: 1.0,
      atmosphere: 0.7,
      conceptMode: "none",
    },
  },
  {
    name: "Supernova",
    accent: 30,
    values: {
      visualMode: 3,
      colorMode: 2,
      paletteHue: 30,
      starSize: 0.22,
      cymaticStrength: 2.6,
      bassGravity: 2.6,
      midCurl: 1.4,
      flowSpeed: 1.4,
      atmosphere: 1.0,
      conceptMode: "bloom",
    },
  },
  {
    name: "Abyss",
    accent: 270,
    values: {
      visualMode: 0,
      colorMode: 0,
      paletteHue: 270,
      starSize: 0.13,
      cymaticStrength: 0.6,
      bassGravity: 0.6,
      midCurl: 0.5,
      flowSpeed: 0.3,
      atmosphere: 0.3,
      backdropBlur: 70,
      conceptMode: "nebula",
    },
  },
];

export interface ParticleCloudPreset {
  name: string;
  accent?: number;
  values: Partial<BioclastParams>;
}

export const PARTICLE_CLOUD_PRESETS: ParticleCloudPreset[] = [
  {
    name: "Core Mass",
    accent: 195,
    values: {
      visualMode: 0,
      colorMode: 3,
      conceptMode: "none",
      starSize: 0.28,
      opacity: 0.9,
      intensity: 1.25,
      flowSpeed: 0.45,
      bassGravity: 2.2,
      midCurl: 1.2,
      trebleShimmer: 0.7,
      cymaticStrength: 2.5,
      fieldTightness: 0.74,
      cameraZoom: 1.16,
      cameraSpin: 0.26,
    },
  },
  {
    name: "Liquid Swarm",
    accent: 168,
    values: {
      visualMode: 0,
      colorMode: 1,
      conceptMode: "none",
      starSize: 0.2,
      opacity: 0.86,
      intensity: 1.1,
      flowSpeed: 1.45,
      bassGravity: 1.3,
      midCurl: 2.2,
      trebleShimmer: 1.35,
      cymaticStrength: 1.2,
      fieldTightness: 1.22,
      cameraZoom: 1.0,
      cameraSpin: 0.72,
    },
  },
  {
    name: "Shock Bloom",
    accent: 24,
    values: {
      visualMode: 0,
      colorMode: 2,
      conceptMode: "bloom",
      starSize: 0.24,
      opacity: 0.94,
      intensity: 1.38,
      flowSpeed: 0.68,
      bassGravity: 2.8,
      midCurl: 1.5,
      trebleShimmer: 0.95,
      cymaticStrength: 3.2,
      fieldTightness: 0.8,
      cameraZoom: 1.26,
      cameraSpin: 0.42,
    },
  },
  {
    name: "Ghost Dust",
    accent: 274,
    values: {
      visualMode: 0,
      colorMode: 0,
      conceptMode: "nebula",
      starSize: 0.17,
      opacity: 0.66,
      intensity: 0.92,
      flowSpeed: 0.22,
      bassGravity: 0.7,
      midCurl: 0.52,
      trebleShimmer: 1.1,
      cymaticStrength: 0.7,
      fieldTightness: 0.96,
      cameraZoom: 0.9,
      cameraSpin: 0.18,
    },
  },
];

// ---------------------------------------------------------------------------
// Framing presets — one-tap camera framings (orbit + flythrough variants)
// ---------------------------------------------------------------------------

interface FramingPreset {
  value: string;
  values: Partial<BioclastParams>;
}

export const FRAMING_PRESETS: FramingPreset[] = [
  // Orbit shots — third-person framing.
  {
    value: "wide",
    values: {
      cameraMode: "orbit",
      cameraZoom: 0.7,
      cameraTilt: 0.45,
      cameraSpin: 0.4,
      cameraOrbit: 0,
    },
  },
  {
    value: "hero",
    values: {
      cameraMode: "orbit",
      cameraZoom: 1.1,
      cameraTilt: 0.55,
      cameraSpin: 0.6,
      cameraOrbit: 0,
    },
  },
  {
    value: "topdown",
    values: {
      cameraMode: "orbit",
      cameraZoom: 1.0,
      cameraTilt: 0.95,
      cameraSpin: 0.3,
      cameraOrbit: 0,
    },
  },
  {
    value: "cinema",
    values: {
      cameraMode: "orbit",
      cameraZoom: 1.4,
      cameraTilt: 0.25,
      cameraSpin: 0.15,
      cameraOrbit: 0,
    },
  },
  {
    value: "closeup",
    values: {
      cameraMode: "orbit",
      cameraZoom: 1.9,
      cameraTilt: 0.4,
      cameraSpin: 0.8,
      cameraOrbit: 0,
    },
  },
  // Flythrough shots — first-person snake through the field.
  {
    value: "fly-slow",
    values: {
      cameraMode: "flythrough",
      cameraZoom: 1.0,
      flythroughSpeed: 0.35,
    },
  },
  {
    value: "fly-cruise",
    values: {
      cameraMode: "flythrough",
      cameraZoom: 1.0,
      flythroughSpeed: 1.0,
    },
  },
  {
    value: "fly-warp",
    values: {
      cameraMode: "flythrough",
      cameraZoom: 1.4,
      flythroughSpeed: 2.4,
    },
  },
];

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface LookPanel {
  /** Re-sync visible state from `renderer.params`. Call after preset apply. */
  refresh(): void;
}

export function buildLookPanel(
  host: HTMLElement,
  renderer: Renderer,
): LookPanel {
  host.innerHTML = "";
  host.classList.add("lookPanel");

  const sliderHandles: Array<{ refresh: () => void }> = [];
  const activeSceneControlHandles: Array<{ refresh: () => void }> = [];

  // -------- Curated scenes (primary entry point) --------
  // Each scene is a complete, opinionated recipe: layers, camera mode,
  // colors, audio reactivity, bloom — all coherent. Power users adjust
  // anything afterward via the toggles + sliders below. Scenes are
  // grouped by mood so the panel reads as a curated library, not a flat
  // dump of 27 names.
  section(host, "Scenes");
  let activeSceneName: string | null = null;
  let activeVariantName: string | null = null;
  const sceneRefreshers: Array<{ refresh: () => void }> = [];
  const moodGroups = scenesByMood();
  moodGroups.forEach((group, groupIndex) => {
    const family = document.createElement("details");
    family.className = "lookPanel__family";
    setupSceneFamilyDisclosure(family, String(group.mood), groupIndex === 0);
    const heading = document.createElement("summary");
    heading.className = "lookPanel__familySummary";
    heading.textContent = group.mood;
    family.appendChild(heading);
    const body = document.createElement("div");
    body.className = "lookPanel__familyBody";
    family.appendChild(body);
    host.appendChild(family);
    const ref = padGrid(
      body,
      group.scenes.map((s) => ({
        label: s.variants?.length ? `${s.name}` : s.name,
        value: s.name,
        accentHue: s.accent,
      })),
      () => activeSceneName,
      (value) => {
        const scene = SCENES.find((s) => s.name === value);
        if (!scene) return;
        applyScenePreset(renderer, scene);
        activeSceneName = scene.name;
        activeVariantName = null;
        renderActiveSceneControls();
        sliderHandles.forEach((s) => s.refresh());
        colorSeg.refresh();
        sceneRefreshers.forEach((r) => r.refresh());
        renderVariantStrip();
      },
    );
    sceneRefreshers.push(ref);
  });
  // Variant chip strip (Original + named variants). Hidden until the
  // active scene actually has variants.
  const variantStrip = document.createElement("div");
  variantStrip.className = "lookPanel__variants";
  variantStrip.hidden = true;
  variantStrip.style.cssText =
    "display:flex;gap:6px;flex-wrap:wrap;padding:8px 0 4px 0;";
  host.appendChild(variantStrip);

  const activeSceneControlsHost = document.createElement("div");
  activeSceneControlsHost.className = "lookPanel__sceneControls";
  activeSceneControlsHost.hidden = true;
  host.appendChild(activeSceneControlsHost);
  let legacyAudioControlsHost: HTMLDivElement | null = null;

  const activeScene = () =>
    activeSceneName ? SCENES.find((s) => s.name === activeSceneName) : null;

  function appendSceneControl(parent: HTMLElement, ctrl: ControlDef): void {
    if (ctrl.kind === "slider") {
      const handle = sliderRow({
        host: parent,
        controlKey: ctrl.key,
        label: ctrl.label,
        sub: ctrl.sub,
        tooltip: ctrl.tooltip,
        minValue: ctrl.min,
        maxValue: ctrl.max,
        stepValue: ctrl.step,
        leftHint: ctrl.leftHint,
        rightHint: ctrl.rightHint,
        get: () => Number(renderer.params[ctrl.key]),
        set: (v) => {
          (renderer.params as unknown as Record<string, unknown>)[ctrl.key] = v;
        },
      });
      activeSceneControlHandles.push(handle);
      return;
    }

    if (ctrl.kind === "toggle") {
      const handle = toggleRow({
        host: parent,
        controlKey: ctrl.key,
        label: ctrl.label,
        sub: ctrl.sub,
        tooltip: ctrl.tooltip,
        get: () => Boolean(renderer.params[ctrl.key]),
        set: (v) => {
          (renderer.params as unknown as Record<string, unknown>)[ctrl.key] = v;
        },
      });
      activeSceneControlHandles.push(handle);
      return;
    }

    const handle = segmentedControl(
      parent,
      ctrl.options.map((option) => ({
        label: option.label,
        value: option.value,
      })),
      () => String(renderer.params[ctrl.key]),
      (v) => {
        (renderer.params as unknown as Record<string, unknown>)[ctrl.key] = v;
      },
    );
    activeSceneControlHandles.push(handle);
  }

  function appendSceneControlSections(
    parent: HTMLElement,
    sections: ControlSection[],
  ): void {
    for (const sectionDef of sections) {
      section(parent, sectionDef.title);
      if (sectionDef.hint) {
        const hint = document.createElement("div");
        hint.className = "lookPanel__hint";
        hint.textContent = sectionDef.hint;
        parent.appendChild(hint);
      }
      for (const ctrl of sectionDef.controls) appendSceneControl(parent, ctrl);
    }
  }

  function renderActiveSceneControls(): void {
    activeSceneControlHandles.length = 0;
    activeSceneControlsHost.innerHTML = "";
    const scene = activeScene();
    if (legacyAudioControlsHost) legacyAudioControlsHost.hidden = Boolean(scene);
    if (!scene) {
      activeSceneControlsHost.hidden = true;
      return;
    }

    const controlSections = filterBySceneProfile({
      sceneName: scene.name,
      sceneType: scene.sceneType ?? "bioclast",
      profile: scene.controlProfile,
      values: scene.values,
    });
    if (!controlSections.length) {
      activeSceneControlsHost.hidden = true;
      return;
    }

    activeSceneControlsHost.hidden = false;
    section(activeSceneControlsHost, "Selected Scene Controls");
    const summary = document.createElement("div");
    summary.className = "lookPanel__hint";
    summary.textContent = `${scene.name}: ${scene.subtitle}`;
    activeSceneControlsHost.appendChild(summary);
    appendSceneControlSections(activeSceneControlsHost, controlSections);
    syncLookPanelSectionDisclosures(activeSceneControlsHost, false);
  }

  const renderVariantStrip = (): void => {
    variantStrip.innerHTML = "";
    const scene = activeScene();
    if (!scene || !scene.variants?.length) {
      variantStrip.hidden = true;
      return;
    }
    variantStrip.hidden = false;
    const makeChip = (
      label: string,
      hue: number,
      isActive: boolean,
      onTap: () => void,
    ): void => {
      const chip = document.createElement("button");
      chip.type = "button";
      chip.className = "lookPanel__variantChip" + (isActive ? " is-active" : "");
      chip.textContent = label;
      chip.style.cssText =
        "padding:5px 11px;border-radius:999px;font:500 11px/1 system-ui,sans-serif;" +
        `border:1px solid hsla(${hue}deg,80%,60%,${isActive ? 0.95 : 0.4});` +
        `background:hsla(${hue}deg,80%,${isActive ? 35 : 16}%,${isActive ? 0.55 : 0.22});` +
        `color:hsl(${hue}deg,90%,${isActive ? 92 : 78}%);cursor:pointer;`;
      chip.addEventListener("click", () => {
        onTap();
        renderVariantStrip();
      });
      variantStrip.appendChild(chip);
    };
    makeChip("Original", scene.accent, activeVariantName === null, () => {
      applyScenePreset(renderer, scene);
      activeVariantName = null;
      renderActiveSceneControls();
      sliderHandles.forEach((s) => s.refresh());
      colorSeg.refresh();
    });
    for (const v of scene.variants) {
      const hue = v.accent ?? scene.accent;
      makeChip(v.name, hue, activeVariantName === v.name, () => {
        applyScenePreset(renderer, scene, v);
        activeVariantName = v.name;
        renderActiveSceneControls();
        sliderHandles.forEach((s) => s.refresh());
        colorSeg.refresh();
      });
    }
  };
  // Wrap mood-section refreshers under the legacy `scenePadsRef` shape
  // so existing call sites (`scenePadsRef.refresh()`) keep working.
  const scenePadsRef: { refresh: () => void } = {
    refresh: () => {
      sceneRefreshers.forEach((r) => r.refresh());
      renderVariantStrip();
    },
  };

  // -------- Layers --------
  // Per-element toggles. Each one independently shows/hides one visual
  // layer. Scenes set these explicitly; flipping a toggle adds or removes
  // that element from the current scene without losing other tuning.
  section(host, "Layers");
  pushToggle(
    "Particle cloud",
    "core particle field",
    "layerParticles",
    "The galaxy of particles. Toggle off only when using a non-particle layer (e.g. Warp) standalone.",
  );
  pushToggle(
    "String strands",
    "curling line strands",
    "layerStrings",
    "Curling line strands swirling through the field on midrange.",
  );
  pushToggle(
    "Lattice rings",
    "concentric ring lattice",
    "layerLattice",
    "Concentric ring lattice in the equatorial plane.",
  );
  pushToggle(
    "Aurora ribbons",
    "drifting overhead ribbons",
    "layerAurora",
    "Wavy ribbons drifting overhead, treble-driven.",
  );
  pushToggle(
    "Warp tunnel",
    "stars rushing past",
    "layerWarp",
    "Camera-locked star tunnel. Best as the main element on its own.",
  );
  pushToggle(
    "Cathedral pillars",
    "vertical columns",
    "layerCathedral",
    "Tall vertical pillars. Pairs well with the Lattice layer.",
  );
  pushToggle(
    "DNA helix",
    "spinning double helix",
    "layerDnaHelix",
    "Two camera-locked particle strands flying past with beat-flashing cross-rungs. Best as the only layer.",
  );
  pushToggle(
    "Pulse vortex",
    "inward spiral, beat shockwaves",
    "layerVortex",
    "Particles spiraling toward a central event horizon, with concentric pulse rings on every kick.",
  );
  pushToggle(
    "Audio tide",
    "live FFT terrain wave",
    "layerTide",
    "Renders the live audio spectrum as a 3D height field that flows toward the camera.",
  );
  pushToggle(
    "Orbital rings",
    "concentric bands tied to bass/mid/treble",
    "layerOrbital",
    "Three concentric particle bands rotating at rates locked to bass / mid / treble.",
  );
  pushToggle(
    "Galaxy flythrough",
    "spiral arms + warp streaks",
    "layerGalaxy",
    "Spiral-arm star disc with bright core and warp-speed streaks streaming past the camera.",
  );
  pushToggle(
    "Aurora forest",
    "vertical ribbons in a circle",
    "layerAuroraForest",
    "A forest of tall vertical aurora ribbons standing around the camera, swaying with mid + treble.",
  );
  pushToggle(
    "Boid flock",
    "swarm reacts to kicks",
    "layerBoids",
    "Glowing swarm with separation/alignment/cohesion. Bass kicks tighten the flock; treble fans it apart.",
  );
  pushToggle(
    "Wind tunnel",
    "organic flow, beat reverses direction",
    "layerWind",
    "Long camera-locked tunnel of particles flowing toward the lens. Strong beats momentarily reverse the flow.",
  );
  pushToggle(
    "Mercury pool",
    "reflective liquid waves",
    "layerMercury",
    "A wide horizontal liquid-metal surface that ripples to the music. Treble drops splash sources, bass heaves the whole pool.",
  );
  pushToggle(
    "Mountain range",
    "fly through endless terrain",
    "layerMountains",
    "Persistent noise-generated mountain terrain. Set Flow speed to fly forward through it; bass amplifies peak height.",
  );
  pushToggle(
    "Energy orb",
    "volumetric flame sphere",
    "layerEnergyOrb",
    "Raymarched volumetric light inside a glowing sphere. Best as the main layer for Fire Core and Ion Bloom.",
  );
  pushToggle(
    "Waveform overlay",
    "audio waveform line on top",
    "layerWaveform",
    "MilkDrop-style audio waveform line drawn camera-locked over every scene. Style picker is in Performance.",
  );

  // -------- Color mode --------
  section(host, "Color Mode");
  const colorSeg = segmentedControl(
    host,
    [
      { label: "Ice", value: 0 },
      { label: "Rivers", value: 1 },
      { label: "Velocity", value: 2 },
      { label: "Density", value: 3 },
    ],
    () => renderer.params.colorMode,
    (v) => {
      renderer.params.colorMode = v as 0 | 1 | 2 | 3;
    },
  );

  // ---- Stub: legacy compositionSeg + concept overrides removed; layer
  // toggles + scenes do their job. Kept as a no-op so the rest of the file
  // (refresh fan-out below) doesn't need surgery. ----

  // -------- Look sliders --------
  section(host, "Look");
  pushSlider(
    "Color Palette",
    "color wheel position",
    "paletteHue",
    0,
    360,
    1,
    "0",
    "360",
    "Where the palette starts on the color wheel.",
  );
  pushSlider(
    "Intensity",
    "global brightness",
    "intensity",
    0,
    4,
    0.01,
    "ghost",
    "blazing",
    "Overdrives particle color brightness. >1 pushes additive blending past full white.",
  );
  pushSlider(
    "Star size",
    "particle dot",
    "starSize",
    0.05,
    1,
    0.01,
    "tiny",
    "huge",
    "Per-particle dot size — bigger reads louder on a beat.",
  );
  pushSlider(
    "Saturation",
    "color vividness",
    "saturation",
    0,
    1,
    0.01,
    "grey",
    "neon",
    "0 strips color to greyscale; 1 is fully saturated.",
  );
  pushSlider(
    "Opacity",
    "particle alpha",
    "opacity",
    0,
    1,
    0.01,
    "ghost",
    "solid",
    "Global transparency of all particles and structures.",
  );

  // -------- Audio reactivity --------
  legacyAudioControlsHost = document.createElement("div");
  legacyAudioControlsHost.className = "lookPanel__legacyAudioControls";
  host.appendChild(legacyAudioControlsHost);
  section(legacyAudioControlsHost, "Audio Reactivity");
  pushSlider(
    "Bass gravity",
    "kick pull",
    "bassGravity",
    0,
    4,
    0.05,
    "off",
    "huge",
    "How much kick/bass pulls particles together and out.",
    legacyAudioControlsHost,
  );
  pushSlider(
    "Mid curl",
    "swirl",
    "midCurl",
    0,
    4,
    0.05,
    "off",
    "storm",
    "How much midrange bends the flow into curling rivers.",
    legacyAudioControlsHost,
  );
  pushSlider(
    "Treble shimmer",
    "lift / sparkle",
    "trebleShimmer",
    0,
    4,
    0.05,
    "off",
    "sparkle",
    "How much treble lifts and twinkles the particles.",
    legacyAudioControlsHost,
  );
  pushSlider(
    "Cymatic strength",
    "beat shockwaves",
    "cymaticStrength",
    0,
    4,
    0.05,
    "soft",
    "violent",
    "Strength of bass-beat shockwave rings expanding outward.",
    legacyAudioControlsHost,
  );
  pushSlider(
    "Flow speed",
    "field current",
    "flowSpeed",
    0,
    4,
    0.01,
    "still",
    "fast",
    "Base current that orbits particles around the galaxy.",
    legacyAudioControlsHost,
  );
  syncLookPanelSectionDisclosures(legacyAudioControlsHost);

  // -------- Camera --------
  section(host, "Camera");
  const dragHint = document.createElement("div");
  dragHint.className = "lookPanel__hint";
  dragHint.textContent =
    "Tip: drag the canvas to orbit + tilt, scroll to zoom.";
  host.appendChild(dragHint);

  // Camera mode segmented (orbit vs flythrough)
  const cameraModeSeg = segmentedControl(
    host,
    [
      { label: "Orbit", value: "orbit" },
      { label: "Flythrough", value: "flythrough" },
    ],
    () => renderer.params.cameraMode,
    (v) => {
      renderer.params.cameraMode = v as "orbit" | "flythrough";
    },
  );

  // -------- Framing transitions --------
  // Snapping camera knobs is jarring; interpolating over ~600 ms lets the
  // shot ease into the new framing. Numeric fields are tweened with a cubic
  // ease-out; non-numeric fields (cameraMode, etc.) snap immediately.
  const TWEEN_MS = 600;
  const TWEEN_KEYS: (keyof BioclastParams)[] = [
    "cameraZoom",
    "cameraTilt",
    "cameraOrbit",
    "cameraSpin",
    "flythroughSpeed",
  ];
  let tween: {
    startedAt: number;
    from: Record<string, number>;
    to: Record<string, number>;
    onDone?: () => void;
  } | null = null;

  const tweenFraming = (values: Partial<BioclastParams>): void => {
    // Snap non-numeric fields right away (e.g. cameraMode), then tween the
    // tween-friendly numeric ones from the current value to the target.
    const numericTo: Record<string, number> = {};
    const numericFrom: Record<string, number> = {};
    for (const k of TWEEN_KEYS) {
      if (k in values) {
        const target = (values as Record<string, unknown>)[k as string];
        if (typeof target === "number") {
          numericTo[k as string] = target;
          numericFrom[k as string] = Number(renderer.params[k]);
        }
      }
    }
    // Apply non-numeric snap fields directly.
    for (const [key, value] of Object.entries(values)) {
      if (TWEEN_KEYS.includes(key as keyof BioclastParams)) continue;
      (renderer.params as unknown as Record<string, unknown>)[key] = value;
    }
    if (Object.keys(numericTo).length === 0) {
      sliderHandles.forEach((s) => s.refresh());
      cameraModeSeg.refresh();
      return;
    }
    tween = {
      startedAt: performance.now(),
      from: numericFrom,
      to: numericTo,
    };
  };

  const tickTween = (): void => {
    if (tween) {
      const now = performance.now();
      const t = Math.min(1, (now - tween.startedAt) / TWEEN_MS);
      const eased = 1 - Math.pow(1 - t, 3);
      for (const key of Object.keys(tween.to)) {
        const from = tween.from[key];
        const to = tween.to[key];
        (renderer.params as unknown as Record<string, number>)[key] =
          from + (to - from) * eased;
      }
      if (t >= 1) {
        const done = tween.onDone;
        tween = null;
        if (done) done();
      }
      // Refresh slider visuals so the user sees the tween live.
      sliderHandles.forEach((s) => s.refresh());
    }
    requestAnimationFrame(tickTween);
  };
  requestAnimationFrame(tickTween);

  // Framing presets — one-tap recipes for common shots.
  const framingPads = padGrid(
    host,
    [
      { label: "Wide", value: "wide" },
      { label: "Hero", value: "hero" },
      { label: "Top-Down", value: "topdown" },
      { label: "Cinematic", value: "cinema" },
      { label: "Closeup", value: "closeup" },
      { label: "Fly · Slow", value: "fly-slow" },
      { label: "Fly · Cruise", value: "fly-cruise" },
      { label: "Fly · Warp", value: "fly-warp" },
    ],
    () => null,
    (value) => {
      const preset = FRAMING_PRESETS.find((f) => f.value === value);
      if (!preset) return;
      tweenFraming(preset.values);
    },
  );
  pushSlider(
    "Zoom",
    "distance from scene",
    "cameraZoom",
    0.15,
    3.5,
    0.01,
    "way out",
    "close",
    "Distance from the scene. Lower values pull way back for a lava-lamp view of the whole field.",
  );
  pushSlider(
    "Tilt",
    "vertical angle",
    "cameraTilt",
    0,
    1,
    0.01,
    "horizon",
    "top-down",
    "Vertical view angle. 0 sees the field edge-on, 1 looks straight down.",
  );
  pushSlider(
    "Manual orbit",
    "yaw offset",
    "cameraOrbit",
    -Math.PI,
    Math.PI,
    0.01,
    "←",
    "→",
    "Manual yaw offset (radians). Drag the canvas left/right to set it too.",
  );
  pushSlider(
    "Auto spin",
    "orbit speed",
    "cameraSpin",
    0,
    3,
    0.01,
    "still",
    "fast",
    "Rate the camera orbits the scene automatically (orbit mode only).",
  );
  pushSlider(
    "Flythrough speed",
    "spaceship pace",
    "flythroughSpeed",
    0.1,
    3,
    0.01,
    "drift",
    "warp",
    "Speed of the flythrough path (only used when Camera mode is Flythrough).",
  );
  pushSlider(
    "Beat dolly",
    "punch on each beat",
    "beatDolly",
    0,
    1,
    0.01,
    "off",
    "punchy",
    "Forward camera lurch on each detected beat (flythrough mode only).",
  );

  // -------- Autopilot --------
  // Cycles through framing presets every N seconds (or on the next beat),
  // so a long clip naturally changes shots without manual tapping.
  pushToggle(
    "Autopilot framing",
    "auto-cycles framing presets",
    "autopilotEnabled",
    "Automatically rotates through framing presets every N seconds for hands-off cinematography.",
  );
  pushToggle(
    "Lock changes to beats",
    "snap on next beat",
    "autopilotBeatLock",
    "When on, autopilot waits for the next strong beat before changing the framing.",
  );
  pushSlider(
    "Autopilot interval",
    "seconds per shot",
    "autopilotInterval",
    2,
    30,
    1,
    "fast",
    "slow",
    "Minimum seconds between autopilot framing changes.",
  );

  let autopilotIdx = 0;
  let autopilotLastFireMs = 0;
  let beatArmed = true;
  const autopilotTick = (): void => {
    if (renderer.params.autopilotEnabled) {
      const nowMs = performance.now();
      const intervalMs = Math.max(2, renderer.params.autopilotInterval) * 1000;
      const elapsed = nowMs - autopilotLastFireMs;
      const intervalReady = elapsed >= intervalMs;
      const beat = renderer.beatLevel;
      // Re-arm beat detection between hits so we only fire once per beat.
      if (beat < 0.2) beatArmed = true;
      const beatHit = beatArmed && beat > 0.55;
      const shouldFire = renderer.params.autopilotBeatLock
        ? intervalReady && beatHit
        : intervalReady;
      if (shouldFire) {
        if (renderer.params.autopilotBeatLock) beatArmed = false;
        autopilotLastFireMs = nowMs;
        const preset = FRAMING_PRESETS[autopilotIdx % FRAMING_PRESETS.length];
        autopilotIdx++;
        tweenFraming(preset.values);
      }
    } else {
      // While disabled, keep the timer fresh so the first fire is "soon"
      // after the user enables it instead of "immediately".
      autopilotLastFireMs = performance.now();
    }
    requestAnimationFrame(autopilotTick);
  };
  requestAnimationFrame(autopilotTick);

  // -------- Field shape --------
  section(host, "Field Shape");
  pushSlider(
    "Field tightness",
    "squeeze / spread",
    "fieldTightness",
    0.4,
    1.6,
    0.01,
    "tight",
    "wide",
    "Squeezes (low) or fans out (high) the entire particle field.",
  );

  // -------- Bloom (neon glow post-processing) --------
  section(host, "Bloom Glow");
  pushToggle(
    "Bloom enabled",
    "neon glow post-fx",
    "bloomEnabled",
    "Adds a soft neon halo around bright pixels. Costs ~2-4ms per frame on most GPUs.",
  );
  pushSlider(
    "Bloom strength",
    "halo intensity",
    "bloomStrength",
    0,
    3,
    0.01,
    "subtle",
    "blazing",
    "How brightly bloomed pixels glow on top of the scene.",
  );
  pushSlider(
    "Bloom radius",
    "halo spread",
    "bloomRadius",
    0,
    1.2,
    0.01,
    "tight",
    "wide",
    "How far the glow blurs out from each bright pixel.",
  );
  pushSlider(
    "Bloom threshold",
    "what blooms",
    "bloomThreshold",
    0,
    1,
    0.01,
    "everything",
    "only bright",
    "Only pixels brighter than this value bloom. 0 = whole scene glows; 1 = almost nothing does.",
  );

  // -------- Trails (frame feedback / MilkDrop-style history) --------
  section(host, "Trails");
  const trailsHint = document.createElement("div");
  trailsHint.className = "lookPanel__hint";
  trailsHint.textContent =
    "Frame-feedback effect — particles leave swirling trails. Strength 0 = off (zero perf cost).";
  host.appendChild(trailsHint);
  pushSlider(
    "Trail strength",
    "how much of last frame to keep",
    "feedbackStrength",
    0, 0.95, 0.01,
    "off", "heavy",
    "0 disables trails entirely. Above 0, the previous frame is blended in each frame.",
  );
  pushSlider(
    "Trail decay",
    "how fast trails fade",
    "feedbackDecay",
    0.8, 1.0, 0.005,
    "fast fade", "slow fade",
    "Closer to 1 = trails persist longer. 0.96 is balanced.",
  );
  pushSlider(
    "Trail zoom",
    "radial pull-in/out",
    "feedbackZoom",
    -0.05, 0.05, 0.001,
    "outward", "inward",
    "Negative pushes trails outward (explosion); positive pulls inward (vortex).",
  );
  pushSlider(
    "Trail rotate",
    "spin the trails",
    "feedbackRotate",
    -0.05, 0.05, 0.001,
    "left", "right",
    "Per-frame rotation. Combine with strength > 0.7 for a hypnotic spiral.",
  );
  pushSlider(
    "Trail drift X",
    "horizontal trail offset",
    "feedbackDriftX",
    -0.02, 0.02, 0.0005,
    "left", "right",
    "Per-frame horizontal drift of the previous frame.",
  );
  pushSlider(
    "Trail drift Y",
    "vertical trail offset",
    "feedbackDriftY",
    -0.02, 0.02, 0.0005,
    "down", "up",
    "Per-frame vertical drift of the previous frame.",
  );

  // -------- Atmosphere --------
  section(host, "Atmosphere");
  pushSlider(
    "Glow",
    "backdrop wash",
    "atmosphere",
    0,
    1.5,
    0.01,
    "off",
    "bright",
    "Strength of the atmospheric color wash behind the visualizer.",
  );
  pushSlider(
    "Blur",
    "backdrop softness",
    "backdropBlur",
    0,
    120,
    1,
    "crisp",
    "soft",
    "Blur radius of the backdrop wash. Does not blur particles.",
  );

  // -------- Reset --------
  const resetBtn = document.createElement("button");
  resetBtn.type = "button";
  resetBtn.className = "lookPanel__reset";
  resetBtn.textContent = "Reset all";
  resetBtn.addEventListener("click", () => {
    Object.assign(renderer.params, DEFAULT_BIOCLAST);
    sliderHandles.forEach((s) => s.refresh());
    colorSeg.refresh();
    activeSceneName = null;
    activeVariantName = null;
    renderActiveSceneControls();
    scenePadsRef.refresh();
  });
  host.appendChild(resetBtn);

  // -------- Slider helper --------
  function pushSlider(
    label: string,
    sub: string,
    key: keyof BioclastParams,
    minValue: number,
    maxValue: number,
    stepValue: number,
    leftHint: string,
    rightHint: string,
    tooltip: string,
    parent: HTMLElement = host,
  ): void {
    const handle = sliderRow({
      host: parent,
      label,
      sub,
      tooltip,
      minValue,
      maxValue,
      stepValue,
      leftHint,
      rightHint,
      get: () => Number(renderer.params[key]),
      set: (v) => {
        // Parameters are typed as numbers (we do not call this for enum
        // keys); a plain assignment to a `Partial<BioclastParams>` keeps the
        // BioclastParams object shape intact.
        (renderer.params as unknown as Record<string, unknown>)[key as string] = v;
      },
    });
    sliderHandles.push(handle);
  }

  // -------- Toggle helper --------
  function pushToggle(
    label: string,
    sub: string,
    key: keyof BioclastParams,
    tooltip: string,
  ): void {
    const handle = toggleRow({
      host,
      label,
      sub,
      tooltip,
      get: () => Boolean(renderer.params[key]),
      set: (v) => {
        (renderer.params as unknown as Record<string, unknown>)[key as string] = v;
      },
    });
    sliderHandles.push(handle);
  }

  syncLookPanelSectionDisclosures(host);

  return {
    refresh(): void {
      sliderHandles.forEach((s) => s.refresh());
      activeSceneControlHandles.forEach((s) => s.refresh());
      colorSeg.refresh();
      cameraModeSeg.refresh();
      framingPads.refresh();
      scenePadsRef.refresh();
    },
  };
}

// ---------------------------------------------------------------------------
// Local DOM helpers
// ---------------------------------------------------------------------------

function section(parent: HTMLElement, text: string): void {
  if (text === "Selected Scene Controls") {
    const el = document.createElement("div");
    el.className = "lookPanel__section lookPanel__section--static";
    el.textContent = text;
    parent.appendChild(el);
    return;
  }

  const el = document.createElement("button");
  el.type = "button";
  el.className = "lookPanel__section";
  el.textContent = text;
  el.setAttribute("aria-expanded", "true");
  parent.appendChild(el);
}

const LOOK_PANEL_SECTION_STATE_KEY = "pulsefx.lookPanel.sectionOpen.v1";
const SCENE_FAMILY_STATE_KEY = "pulsefx.sceneFamilies.open.v1";

function readOpenSet(storageKey: string): Set<string> | null {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.map(String)) : null;
  } catch {
    return null;
  }
}

function writeOpenSet(storageKey: string, values: Set<string>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...values]));
  } catch {
    /* private mode */
  }
}

function setupSceneFamilyDisclosure(
  family: HTMLDetailsElement,
  label: string,
  defaultOpen: boolean,
): void {
  const openSet = readOpenSet(SCENE_FAMILY_STATE_KEY);
  family.open = openSet ? openSet.has(label) : defaultOpen;
  family.addEventListener("toggle", () => {
    const next = readOpenSet(SCENE_FAMILY_STATE_KEY) ?? new Set<string>();
    if (family.open) next.add(label);
    else next.delete(label);
    writeOpenSet(SCENE_FAMILY_STATE_KEY, next);
  });
}

function syncLookPanelSectionDisclosures(
  parent: HTMLElement,
  defaultOpen = true,
): void {
  wrapLookPanelSectionBodies(parent);
  const saved = readOpenSet(LOOK_PANEL_SECTION_STATE_KEY);
  parent.querySelectorAll<HTMLButtonElement>(":scope > .lookPanel__section:not(.lookPanel__section--static)").forEach((heading) => {
    if (heading.dataset.collapseReady === "1") return;
    const label = heading.textContent?.trim() || "Section";
    const isOpen = saved ? saved.has(label) : defaultOpen;
    const apply = (open: boolean): void => {
      heading.setAttribute("aria-expanded", open ? "true" : "false");
      heading.classList.toggle("is-collapsed", !open);
      const body = heading.nextElementSibling as HTMLElement | null;
      if (body?.classList.contains("lookPanel__sectionBody")) {
        body.hidden = !open;
      }
    };
    heading.addEventListener("click", () => {
      const nextOpen = heading.getAttribute("aria-expanded") !== "true";
      apply(nextOpen);
      const openSet = new Set<string>();
      parent.querySelectorAll<HTMLButtonElement>(":scope > .lookPanel__section:not(.lookPanel__section--static)").forEach((item) => {
        const itemLabel = item.textContent?.trim();
        if (itemLabel && item.getAttribute("aria-expanded") === "true") {
          openSet.add(itemLabel);
        }
      });
      writeOpenSet(LOOK_PANEL_SECTION_STATE_KEY, openSet);
    });
    heading.dataset.collapseReady = "1";
    apply(isOpen);
  });
}

function wrapLookPanelSectionBodies(parent: HTMLElement): void {
  const headings = Array.from(
    parent.querySelectorAll<HTMLElement>(":scope > .lookPanel__section"),
  );
  for (const heading of headings) {
    if (heading.classList.contains("lookPanel__section--static")) continue;
    const existing = heading.nextElementSibling as HTMLElement | null;
    if (existing?.classList.contains("lookPanel__sectionBody")) continue;

    const body = document.createElement("div");
    body.className = "lookPanel__sectionBody";
    let next = heading.nextSibling;
    while (next) {
      if (
        next instanceof HTMLElement &&
        next.classList.contains("lookPanel__section")
      ) {
        break;
      }
      const move = next;
      next = next.nextSibling;
      body.appendChild(move);
    }
    heading.after(body);
  }
}

interface SliderArgs {
  host: HTMLElement;
  controlKey?: keyof BioclastParams;
  label: string;
  sub: string;
  tooltip: string;
  minValue: number;
  maxValue: number;
  stepValue: number;
  leftHint: string;
  rightHint: string;
  get: () => number;
  set: (v: number) => void;
}

function sliderRow(args: SliderArgs): { refresh: () => void } {
  const row = document.createElement("div");
  row.className = "lookPanel__row";
  if (args.controlKey) row.dataset.controlKey = args.controlKey;
  row.title = args.tooltip;
  row.innerHTML = `
    <div class="lookPanel__rowTop">
      <div>
        <span class="lookPanel__label">${args.label}</span>
        <span class="lookPanel__sub">${args.sub}</span>
      </div>
      <span class="lookPanel__val"></span>
    </div>
    <div class="lookPanel__range">
      <div class="lookPanel__track"></div>
      <div class="lookPanel__fill"></div>
      <input type="range" min="${args.minValue}" max="${args.maxValue}" step="${args.stepValue}" />
    </div>
    <div class="lookPanel__ends">
      <span>${args.leftHint}</span>
      <span>${args.rightHint}</span>
    </div>
  `;
  const input = row.querySelector("input") as HTMLInputElement;
  const fill = row.querySelector(".lookPanel__fill") as HTMLElement;
  const valEl = row.querySelector(".lookPanel__val") as HTMLElement;

  const fmt = (v: number): string => {
    if (Math.abs(v) < 1) return v.toFixed(3);
    if (Math.abs(v) < 100) return v.toFixed(2);
    return Math.round(v).toString();
  };

  const refresh = (): void => {
    const v = args.get();
    input.value = String(v);
    const pct =
      ((v - args.minValue) / (args.maxValue - args.minValue || 1)) * 100;
    fill.style.width = `${Math.max(0, Math.min(100, pct))}%`;
    valEl.textContent = fmt(v);
  };

  input.addEventListener("input", () => {
    const v = Number(input.value);
    args.set(v);
    refresh();
  });
  refresh();
  args.host.appendChild(row);
  return { refresh };
}

interface ToggleArgs {
  host: HTMLElement;
  controlKey?: keyof BioclastParams;
  label: string;
  sub: string;
  tooltip: string;
  get: () => boolean;
  set: (v: boolean) => void;
}

function toggleRow(args: ToggleArgs): { refresh: () => void } {
  const row = document.createElement("label");
  row.className = "lookPanel__toggleRow";
  if (args.controlKey) row.dataset.controlKey = args.controlKey;
  row.title = args.tooltip;
  row.innerHTML = `
    <input type="checkbox" />
    <div class="lookPanel__toggleText">
      <span class="lookPanel__label">${args.label}</span>
      <span class="lookPanel__sub">${args.sub}</span>
    </div>
  `;
  const input = row.querySelector("input") as HTMLInputElement;
  const refresh = (): void => {
    input.checked = args.get();
  };
  input.addEventListener("change", () => {
    args.set(input.checked);
  });
  refresh();
  args.host.appendChild(row);
  return { refresh };
}

interface PadOption<T extends string | number> {
  label: string;
  value: T;
  accentHue?: number;
}

function padGrid<T extends string | number>(
  parent: HTMLElement,
  options: PadOption<T>[],
  getActive: () => T | null,
  onClick: (v: T) => void,
): { refresh: () => void } {
  const grid = document.createElement("div");
  grid.className = "lookPanel__pads";
  const buttons: HTMLButtonElement[] = [];
  for (const opt of options) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "lookPanel__pad";
    btn.textContent = opt.label;
    btn.dataset.value = String(opt.value);
    if (opt.accentHue !== undefined) {
      btn.style.borderColor = `hsla(${opt.accentHue}deg, 80%, 60%, 0.5)`;
      btn.style.color = `hsl(${opt.accentHue}deg, 80%, 75%)`;
    }
    btn.addEventListener("click", () => {
      onClick(opt.value);
      refresh();
    });
    grid.appendChild(btn);
    buttons.push(btn);
  }
  parent.appendChild(grid);

  const refresh = (): void => {
    const active = getActive();
    for (const btn of buttons) {
      btn.classList.toggle(
        "is-active",
        active !== null && btn.dataset.value === String(active),
      );
    }
  };
  refresh();
  return { refresh };
}

function segmentedControl<T extends number | string>(
  parent: HTMLElement,
  options: { label: string; value: T }[],
  getActive: () => T,
  onClick: (v: T) => void,
): { refresh: () => void } {
  const wrap = document.createElement("div");
  wrap.className = "lookPanel__segments";
  const buttons: HTMLButtonElement[] = [];
  for (const opt of options) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "lookPanel__segment";
    btn.textContent = opt.label;
    btn.dataset.value = String(opt.value);
    btn.addEventListener("click", () => {
      onClick(opt.value);
      refresh();
    });
    wrap.appendChild(btn);
    buttons.push(btn);
  }
  parent.appendChild(wrap);

  const refresh = (): void => {
    const active = getActive();
    for (const btn of buttons) {
      btn.classList.toggle(
        "is-active",
        btn.dataset.value === String(active),
      );
    }
  };
  refresh();
  return { refresh };
}
